import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import './Dashboard.css'; // Assuming global styles are here

const CustomerRegisterPage = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState(1); // 1: Details, 2: OTP
    const [timer, setTimer] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState({});
    
    const [formData, setFormData] = useState({
        phone: "",
        name: "",
        city: "",
        state: "",
        otp: "",
        allAccepted: false
    });

    // Countdown Timer logic
    useEffect(() => {
        let interval;
        if (timer > 0) {
            interval = setInterval(() => setTimer(prev => prev - 1), 1000);
        }
        return () => clearInterval(interval);
    }, [timer]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
    };

    const handleSendOtp = async (e) => {
        e.preventDefault();
        setErrors({});
        setIsLoading(true);

        const formattedPhone = `+91${formData.phone}`;

        try {
            const response = await fetch('/api/v1/auth/otp/send', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ phone: formattedPhone, purpose: "LOGIN" })
            });

            if (response.ok) {
                setStep(2);
                setTimer(30);
            } else {
                const data = await response.json();
                setErrors({ general: data.message || "Failed to send OTP. Try again." });
            }
        } catch (err) {
            setErrors({ general: "Network error. Please check your internet." });
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerifyAndRegister = async (e) => {
        e.preventDefault();
        if (!formData.allAccepted) {
            setErrors({ checkbox: "You must accept the terms to continue." });
            return;
        }

        setIsLoading(true);
        try {
            const payload = {
                ...formData,
                phone: `+91${formData.phone}`,
                purpose: "LOGIN"
            };

            const response = await fetch('/api/v1/auth/otp/verify', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                const authData = await response.json();
                localStorage.setItem('customerAccessToken', authData.accessToken);
                localStorage.setItem('customerRefreshToken', authData.refreshToken);
                navigate('/main');
            } else {
                setErrors({ otp: "Invalid OTP. Please try again." });
            }
        } catch (err) {
            setErrors({ general: "Registration failed. Try later." });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="registration-container">
            <div className="registration-card">
                <form onSubmit={step === 1 ? handleSendOtp : handleVerifyAndRegister} className="registration-form">
                    <h2>{step === 1 ? "Create Account" : "Verify Number"}</h2>
                    
                    {errors.general && <div className="error-banner">{errors.general}</div>}

                    {step === 1 ? (
                        <>
                            <div className="form-group">
                                <label>Mobile Number</label>
                                <div className="phone-input-group">
                                    <span>+91</span>
                                    <input 
                                        name="phone" 
                                        type="tel" 
                                        placeholder="10 digit number"
                                        value={formData.phone} 
                                        onChange={handleChange} 
                                        maxLength="10" 
                                        required 
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Full Name</label>
                                <input name="name" type="text" placeholder="Enter your name" onChange={handleChange} required />
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>City</label>
                                    <input name="city" type="text" placeholder="Your City" onChange={handleChange} required />
                                </div>
                                <div className="form-group">
                                    <label>State</label>
                                    <input name="state" type="text" placeholder="Your State" onChange={handleChange} required />
                                </div>
                            </div>

                            <button type="submit" disabled={isLoading} className="submit-btn">
                                {isLoading ? "Processing..." : "Get OTP"}
                            </button>
                        </>
                    ) : (
                        <>
                            <p className="info-text">OTP sent to <b>+91 {formData.phone}</b></p>
                            
                            <div className="form-group">
                                <label>Enter 6-Digit OTP</label>
                                <input 
                                    name="otp" 
                                    type="text" 
                                    placeholder="000000"
                                    value={formData.otp} 
                                    onChange={handleChange} 
                                    maxLength="6" 
                                    required 
                                />
                                {errors.otp && <span className="error-text">{errors.otp}</span>}
                            </div>

                            <div className="checkbox-group">
                                <label className="checkbox-label">
                                    <input 
                                        name="allAccepted" 
                                        type="checkbox" 
                                        onChange={handleChange} 
                                        required 
                                    />
                                    I agree to share data for services.
                                </label>
                                {errors.checkbox && <span className="error-text">{errors.checkbox}</span>}
                            </div>

                            <button type="submit" disabled={isLoading} className="submit-btn">
                                {isLoading ? "Verifying..." : "Complete Registration"}
                            </button>

                            <div className="timer-section">
                                {timer > 0 ? (
                                    <p className="timer-text">Resend OTP in {timer}s</p>
                                ) : (
                                    <span className="resend-link" onClick={() => setStep(1)}>
                                        Incorrect number? Go back
                                    </span>
                                )}
                            </div>
                        </>
                    )}
                    
                    <p className="footer-link">
                        Already have an account? <Link to="/customer-login">Login here</Link>
                    </p>
                </form>
            </div>
        </div>
    );
};

export default CustomerRegisterPage;
